import {
    Box,
    HStack,
    SimpleGrid,
    useRadio,
    useRadioGroup,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { Heading, Text } from '@/components/v4';
import { DR_TRUNK_TYPE_NAME, OC_TRUNK_TYPE_NAME } from '../constants';
import { TRUNK_CHOICES } from '../utils/bulkUpdate';

/** Trunk cards in order, limited to the trunk types the company has. */
const getTrunkOptions = (tenant) =>
    [
        {
            value: TRUNK_CHOICES.KEEP,
            label: 'Keep current',
            description: 'Trunk will be unchanged',
        },
        {
            value: TRUNK_CHOICES.REMOVE,
            label: 'Remove trunk',
            description: 'Number will be disabled',
            labelColor: 'red.500',
        },
        tenant.oc && {
            value: TRUNK_CHOICES.OC,
            label: OC_TRUNK_TYPE_NAME,
            description: 'Number will be enabled',
        },
        tenant.dr && {
            value: TRUNK_CHOICES.DR,
            label: DR_TRUNK_TYPE_NAME,
            description: 'Number will be enabled',
        },
    ].filter(Boolean);

// Same selectable card as the Confirm Transfer direction options.
const TrunkOptionCard = ({ option, darkMode, ...radioProps }) => {
    const { getInputProps, getCheckboxProps, state } = useRadio(radioProps);
    const isChecked = state.isChecked;

    let bg = darkMode ? 'dark.tcap' : 'gray.50';
    if (isChecked) bg = darkMode ? 'whiteAlpha.100' : 'blue.50';

    return (
        <Box as="label" height="100%">
            <input {...getInputProps()} />
            <Box
                {...getCheckboxProps()}
                cursor="pointer"
                borderRadius="md"
                px={4}
                py={3}
                height="100%"
                bg={bg}
                border={isChecked ? '2px solid' : '1px solid'}
                borderColor={
                    isChecked
                        ? 'brand.500'
                        : darkMode
                          ? 'dark.tcap-borderGray'
                          : 'gray.200'
                }
                _hover={{ borderColor: 'brand.500' }}
                _focusVisible={{ boxShadow: 'outline' }}>
                <HStack justify="space-between" mb={1}>
                    <Heading
                        as="h4"
                        size="sm"
                        marginBottom={0}
                        color={option.labelColor}>
                        {option.label}
                    </Heading>
                    <Box
                        aria-hidden
                        boxSize="18px"
                        flexShrink={0}
                        borderRadius="full"
                        border="2px solid"
                        borderColor={isChecked ? 'brand.500' : 'gray.400'}
                        display="flex"
                        alignItems="center"
                        justifyContent="center">
                        {isChecked && (
                            <Box
                                boxSize="8px"
                                borderRadius="full"
                                bg="brand.500"
                            />
                        )}
                    </Box>
                </HStack>
                <Text fontSize="sm" marginBottom={0} color="gray.500">
                    {option.description}
                </Text>
            </Box>
        </Box>
    );
};

const TrunkOptionCards = ({ tenant, value, onChange }) => {
    const { darkMode } = useSelector((state) => state.settings);
    const { getRootProps, getRadioProps } = useRadioGroup({
        name: 'bulkTrunk',
        value,
        onChange,
    });

    return (
        <SimpleGrid {...getRootProps()} columns={2} spacing={3}>
            {getTrunkOptions(tenant).map((option) => (
                <TrunkOptionCard
                    key={option.value}
                    option={option}
                    darkMode={darkMode}
                    {...getRadioProps({ value: option.value })}
                />
            ))}
        </SimpleGrid>
    );
};

export default TrunkOptionCards;
